import Vue from 'vue';
import Router from 'vue-router';
import store from './store';
import routerHelper from '@/helpers/routerHelper';
import { logPageView } from '@/helpers/cieloLogger';

Vue.use(Router);

// Error views
const NotFoundView = () => import('@/views/Errors/NotFound');
const InvalidLinkView = () => import('@/views/Errors/InvalidLink');
const InvalidOrder = () => import('@/views/Errors/InvalidOrder');
const InvalidUrl = () => import('@/views/Errors/InvalidUrl');

/**
 * Lazy views
 */
const MainPageView = () => import('@/views/MainPage');
const ChangePaymentTypeView = () => import('@/views/ChangePaymentType');
const CardPaymentView = () => import('@/views/CardPayment/CardPayment');
const CardPaymentInstallmentsView = () => import('@/views/CardInstallments');
const ShippingAddressView = () => import('@/views/ShippingAddress');
const PersonalDataView = () => import('@/views/PersonalData');
const PaymentSuccessView = () => import('@/views/PaymentSuccess/PaymentSuccess');
const PixFinalizeView = () => import('@/views/PixFinalize');
const QrCodePayView = () => import('@/views/QrCodePay/QrCodePay');
const QrCodePayFinalizeView = () => import('@/views/QrCodePayFinalize');
const ClickToPayView = () => import('@/views/ClickToPay/ClickToPay');
const UserValidationC2PView = () => import('@/views/ClickToPay/steps/UserValidation');
const CardListC2PView = () => import('@/views/ClickToPay/steps/CardList');
const IdLookUpAuthenticationC2PView = () => import('@/views/ClickToPay/steps/IdLookUpAuthentication');
const UnifiedCardPaymentC2PView = () => import('@/views/ClickToPay/steps/UnifiedCardPayment');
const CardDataView = () => import('@/views/CardPayment/steps/CardData');
const GooglePayCardInstallmentsView = () => import('@/views/GooglePay/CardInstallments');
const GooglePayBillingAddressView = () => import('@/views/GooglePay/BillingAddress');
const ApplePayCardInstallmentsView = () => import('@/views/ApplePay/CardInstallments');
const OrderStatusView = () => import('@/views/OrderStatus');
const ChangePixTypeView = () => import('@/views/ChangePixType');
const PixOpenFinanceView = () => import('@/views/PixOpenFinance/PixOpenFinance');
const PixOpenFinanceBanksListView = () => import('@/views/PixOpenFinance/steps/BanksList');

const routes = [
    { path: '*', name: 'NotFound', component: NotFoundView },
    { path: '/invalid-link', name: 'InvalidLink', component: InvalidLinkView },
    { path: '/', name: 'MainPage', alias: '/main', component: MainPageView },
    { path: '/invalid-url/', name: 'InvalidUrl', component: InvalidUrl },
    { path: '/order-status-error/', name: 'InvalidOrder', component: InvalidOrder },
    {
        path: '/order-status',
        meta: {
            title: 'orderStatus',
        },
        name: 'OrderStatus',
        component: OrderStatusView
    },
    { path: '/change-payment-type', name: 'ChangePaymentType', component: ChangePaymentTypeView, meta: { preorderRequired: true } },
    {
        path: '/payment-method/credit-card',
        meta: {
            title: 'paymentByCreditCard',
            preorderRequired: true
        },
        component: CardPaymentView,
        children: [
            { path: 'card-data-cred', alias: '/', name: 'CardDataCred', component: CardDataView },
        ]
    },
    {
        path: '/payment-method/debit-card',
        meta: {
            title: 'paymentByDebitCard',
            preorderRequired: true
        },
        component: CardPaymentView,
        children: [
            { path: 'card-data-debit', alias: '/', name: 'CardDataDebit', component: CardDataView },
        ]
    },
    {
        path: '/payment-method/voucher',
        meta: {
            title: 'paymentByVoucherCard',
            preorderRequired: true
        },
        component: CardPaymentView,
        children: [
            { path: 'card-data-voucher', alias: '/', name: 'CardDataVoucher', component: CardDataView },
        ]
    },
    {
        path: '/payment-method/qr-code-pay',
        meta: {
            title: 'Pagamento por QR Code Pay',
            preorderRequired: true
        },
        component: QrCodePayView,
        children: [
            { path: 'installments', name: 'QrCodePayInstallments', component: CardPaymentInstallmentsView, meta: { propBeingEdited: 'pay-installments' } }
        ]
    },
    { path: '/shipping-address', name: 'ShippingAddress', component: ShippingAddressView, meta: { preorderRequired: true } },
    { path: '/personal-data', name: 'PersonalData', component: PersonalDataView, meta: { preorderRequired: true } },
    { path: '/payment-success', name: 'PaymentSuccess', component: PaymentSuccessView, meta: { preorderRequired: true } },
    { path: '/pix/waiting-payment', name: 'PixFinalizeScreen', component: PixFinalizeView, meta: { preorderRequired: true } },
    { path: '/qr-code-pay/waiting-payment', name: 'QrCodePayFinalizeScreen', component: QrCodePayFinalizeView, meta: { preorderRequired: true } },
    
    {
        path: '/click-pay',
        name: 'ClickToPay',
        component: ClickToPayView,
        meta: { preorderRequired: true },
        children: [
            { path: 'user-validation', alias: '/', name: 'UserValidationC2P', component: UserValidationC2PView },
            { path: 'card-list', name: 'CardListC2P', component: CardListC2PView },
            { path: 'lookup-contacts', name: 'IdLookUpAuthenticationC2P', component: IdLookUpAuthenticationC2PView },
            { path: 'new-card', name: 'UnifiedCardPaymentC2P', component: UnifiedCardPaymentC2PView },
        ]
    },

    {
        path: '/payment-method/multibenefits',
        meta: {
            title: 'paymentByMultibenefits',
            preorderRequired: true
        },
        component: CardPaymentView,
        children: [
            { path: 'card-data-multibenefits', alias: '/', name: 'CardDataMultibenefits', component: CardDataView },
        ]
    },
    
    {
        path: '/payment-method/google-pay',
        meta: {
            title: 'paymentByGooglePay',
            preorderRequired: true
        },
        component: CardPaymentView,
        children: [
            { path: 'card-installments', alias: '/', name: 'GooglePayCardInstallments', component: GooglePayCardInstallmentsView },
            { path: 'billing-address', alias: '/', name: 'GooglePayBillingAddress', component: GooglePayBillingAddressView },
        ]
    },
    
    {
        path: '/payment-method/apple-pay',
        meta: {
            title: 'paymentByGooglePay',
            preorderRequired: true
        },
        component: CardPaymentView,
        children: [
            { path: 'card-installments', alias: '/', name: 'ApplePayCardInstallments', component: ApplePayCardInstallmentsView }
        ]
    },

    { path: '/change-pix-type', name: 'ChangePixType', component: ChangePixTypeView, meta: { preorderRequired: true } },
    {
        path: '/pix/open-finance',
        meta: {
            title: 'paymentByPixOpenFinance',
            preorderRequired: true
        },
        component: PixOpenFinanceView,
        children: [
            { path: 'banks-list', alias: '/', name: 'PixOpenFinanceBanksList', component: PixOpenFinanceBanksListView },
        ]
    },


];

const router = new Router({
    mode: 'hash',
    routes
});

/**
 * Guardiões de rota.
 */
router.beforeEach((to, from, next) => {
    if (routerHelper.PreorderIsRequiredButItHasNotBeenGiven(to))
        return routerHelper.StopNavigationAndRedirectToMainPage(to, next); // Cancela a navegação

    if(to.path !== '/' ) {
        logPageView(to.path);
    }

    // Caso nenhuma condição acima execute, atualiza o pipeline e segue para a próxima rota
    store.commit('pipelineV2/setActualStep', to.name);
    next();
});

router.beforeResolve((to, from, next) => {
    next();
});

export default router;
